import * as React from 'react';

import { cn } from '../base/utils';

export const MEDIA_PREVIEW_ASPECT_RATIOS = ['1:1', '4:3', '3:4', '16:9', '9:16'] as const;

export type MediaPreviewRatio = (typeof MEDIA_PREVIEW_ASPECT_RATIOS)[number];
export type MediaPreviewAspectRatio = 'square' | 'video' | 'auto' | MediaPreviewRatio;
export type MediaPreviewFit = 'contain' | 'cover';

export const resolveMediaPreviewAspectRatioValue = (aspectRatio: MediaPreviewRatio = '1:1'): number => {
  const [width, height] = aspectRatio.split(':').map(Number);
  return width / height;
};

export const mediaPreviewFrameClassName = ({
  aspectRatio = 'square',
  maxSquareHeight = true,
  className,
}: {
  aspectRatio?: MediaPreviewAspectRatio;
  maxSquareHeight?: boolean;
  className?: string;
} = {}) => cn(
  'w-full overflow-hidden bg-muted',
  (aspectRatio === 'square' || aspectRatio === '1:1') && 'aspect-square',
  (aspectRatio === 'square' || aspectRatio === '1:1') && maxSquareHeight && 'max-h-[300px]',
  aspectRatio === '4:3' && 'aspect-[4/3]',
  aspectRatio === '3:4' && 'aspect-[3/4]',
  (aspectRatio === 'video' || aspectRatio === '16:9') && 'aspect-video',
  aspectRatio === '9:16' && 'aspect-[9/16]',
  className,
);

export interface MediaPreviewFrameProps extends React.HTMLAttributes<HTMLDivElement> {
  aspectRatio?: MediaPreviewAspectRatio;
  maxSquareHeight?: boolean;
}

export const MediaPreviewFrame = React.forwardRef<HTMLDivElement, MediaPreviewFrameProps>(
  ({ aspectRatio = 'square', maxSquareHeight = true, className, ...props }, ref) => (
    <div ref={ref} className={mediaPreviewFrameClassName({ aspectRatio, maxSquareHeight, className })} {...props} />
  ),
);
MediaPreviewFrame.displayName = 'MediaPreviewFrame';

export interface MediaPreviewImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fit?: MediaPreviewFit;
}

export const MediaPreviewImage = React.forwardRef<HTMLImageElement, MediaPreviewImageProps>(
  ({ fit = 'contain', className, ...props }, ref) => (
    <img
      ref={ref}
      className={cn('block size-full', fit === 'contain' ? 'object-contain' : 'object-cover', className)}
      {...props}
    />
  ),
);
MediaPreviewImage.displayName = 'MediaPreviewImage';
